import { useState, useRef, useEffect } from "react";

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function ChatInput({ onSend, disabled, placeholder }: ChatInputProps) {
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, [disabled]);

  // Auto-grow the textarea up to ~6 rows, but never shorter than the
  // Send button (h-11 = 44px) so they stay visually aligned.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.max(44, Math.min(el.scrollHeight, 144)) + "px";
  }, [value]);

  const handleSubmit = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Desktop: Enter sends, Shift+Enter newline.
    // Mobile: Enter ALWAYS makes a newline (no good UA detection from
    // keyboard event alone; we keep desktop behavior here and rely on
    // the prominent Send button for touch users).
    if (e.key === "Enter" && !e.shiftKey && !isLikelyMobile()) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const canSend = !disabled && !!value.trim();

  return (
    <div className="relative shrink-0 border-t border-white/15 bg-deep px-3 pt-2.5 pb-3 safe-bottom">
      <div className="flex items-end gap-2">
        <textarea
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || "Ask me to plan your trip..."}
          disabled={disabled}
          rows={1}
          enterKeyHint="send"
          className="flex-1 min-w-0 min-h-[44px] px-3.5 py-2.5 leading-5 border border-white/20 bg-white/8 text-ink rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-accent text-sm disabled:opacity-50 max-h-36 placeholder:text-ink-light"
        />
        <button
          onClick={handleSubmit}
          disabled={!canSend}
          aria-label="Send message"
          className="shrink-0 inline-flex items-center justify-center gap-1.5 h-11 px-4 bg-accent text-deep rounded-xl font-bold text-sm shadow-md shadow-accent/30 hover:bg-accent-light disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none transition-all active:scale-95"
        >
          <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            className="w-4 h-4"
            aria-hidden="true"
          >
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
          </svg>
          <span>Send</span>
        </button>
      </div>
    </div>
  );
}

// Best-effort mobile detection so Enter doesn't accidentally send when the
// user is just trying to make a newline on a phone keyboard. Touch + small
// screen is the heuristic. This is intentionally conservative — desktop
// users keep the Enter-to-send shortcut.
function isLikelyMobile(): boolean {
  if (typeof window === "undefined") return false;
  return (
    "ontouchstart" in window &&
    Math.min(window.innerWidth, window.innerHeight) < 640
  );
}
